import React from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { every, filter, pickBy } from "lodash";

import { tAbandonedCart, tVisualConnectionStatus } from "tmt_api/src/types/types.validator";

import { PdStack, PdText } from "src/components/layout";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { getCartsAbandoneds } from "src/util/apiNew";
import { PdCheckbox } from "src/components/v2/forms/checkbox";
import {
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/v2/layout/table";
import { formatMoney } from "src/util/gFormatting";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { useSendAbandonedCartPromotionEmail } from "src/hooks/modals";
import { useSelector } from "react-redux";
import { PdBadge, PdBadgeLabel, tPdBadgeColorScheme } from "src/components/v2/layout";

export type tUpdatedPayload = {
  isSelected?: boolean;
};

export type tUpdated = {
  [rowId: string]: tUpdatedPayload;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const getColorSchemeFromConnectionStatus = (status: tVisualConnectionStatus): tPdBadgeColorScheme => {
  if (status === "Declined") return "red";
  if (status === "Pending") return "yellow";
  if (status === "Invited") return "orange";
  return "green";
};

const CellEmail: React.FC<{ row: tAbandonedCart; onSuccess?: () => void }> = ({ row, onSuccess }) => {
  const rCompany = useSelector((state) => state.CompanyReducer);
  const sendAbandonedCartPromotionEmail = useSendAbandonedCartPromotionEmail();

  const onPress = async () => {
    const response = await sendAbandonedCartPromotionEmail.openAsync({
      cartIds: [row.id],
      brandName: rCompany.type === "brand" ? rCompany.name : "",
      contactFirstName: rCompany.type === "brand" ? rCompany.contactPerson ?? "" : "",
    });
    if (response.status === "closed") return;
    onSuccess?.();
  };

  if (row.status === "recovered") return <PdTableCellText>-</PdTableCellText>;

  let buttonLabel = row.triesEmailSent === 1 ? "Send 2nd E-mail" : "Write E-mail";
  if (row.status === "declined") buttonLabel = "Retailer Declined";
  if (!row.canSendEmail) buttonLabel = row.triesEmailSent >= 2 ? "Not Recovered" : "E-mail sent!";

  return (
    <PdButton size="sm" isDisabled={!row.canSendEmail || row.status === "declined"} onPress={onPress}>
      <PdButtonLabel>{buttonLabel}</PdButtonLabel>
    </PdButton>
  );
};

const P03_03_AbandonedCarts: React.FC = () => {
  const { data, refetch, isLoading } = useFocusableQuery(getCartsAbandoneds);
  const rCompany = useSelector((state) => state.CompanyReducer);
  const [updated, setUpdated] = React.useState<tUpdated>({});
  const isAllSelected = React.useMemo(() => {
    const availableRows = data?.filter((row) => row.canSendEmail && row.status !== "declined") ?? [];
    return (
      availableRows.length > 0 &&
      Object.keys(updated).length === availableRows.length &&
      every(updated, (u) => u.isSelected === true)
    );
  }, [data, updated]);
  const isAtLeastTwoSelected = React.useMemo(() => filter(updated, (u) => u.isSelected === true).length > 1, [updated]);

  const handleOnPressToggleAll = React.useCallback(
    (isSelected: boolean) => {
      setUpdated((prev) => {
        const next: tUpdated = { ...prev };
        for (const row of data ?? []) {
          if (row.canSendEmail && row.status !== "declined") {
            next[row.id] = {
              ...prev[row.id],
              isSelected,
            };
          }
        }
        return next;
      });
    },
    [data],
  );

  const updateRow = React.useCallback((row: tAbandonedCart, payload: Partial<tUpdatedPayload>) => {
    setUpdated((prev) => {
      const next: tUpdated = { ...prev, [row.id]: { ...prev[row.id], ...payload } };
      return next;
    });
  }, []);

  const columns: tColumn<tAbandonedCart>[] = React.useMemo(
    () => [
      {
        id: "id",
        header: () => (
          <PdCheckbox
            value={isAllSelected}
            onChange={(value) => handleOnPressToggleAll(value)}
            style={styles.alignEnd}
          />
        ),
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row }) => {
          if (row.status === "recovered" || row.triesEmailSent >= 2) return null;
          return (
            <PdCheckbox
              isDisabled={!row.canSendEmail || row.status === "declined"}
              value={!!updated[row.id]?.isSelected}
              onChange={(value) => updateRow(row, { isSelected: value })}
            />
          );
        },
        isSortable: false,
        style: [styles.tableColumnXs],
      },
      {
        id: "dateInSeconds",
        header: "Date",
        accessor: (originalRow) => (originalRow.dateInSeconds ? formatDate(originalRow.dateInSeconds * 1000) : "-"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
      {
        id: "storeName",
        header: "Store Name",
        accessor: (originalRow) => originalRow.storeName || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "contactName",
        header: "Contact Name",
        accessor: (originalRow) => originalRow.contactName || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "connectionStatus",
        header: "Connection Status",
        accessor: (originalRow) => originalRow.connectionStatus,
        Cell: ({ value, row }) =>
          row.connectionStatus ? (
            <PdBadge colorScheme={getColorSchemeFromConnectionStatus(row.connectionStatus)}>
              <PdBadgeLabel>{value}</PdBadgeLabel>
            </PdBadge>
          ) : (
            <PdTableCellText>-</PdTableCellText>
          ),
        style: styles.tableColumnMd,
      },
      {
        id: "totalCartAmount",
        header: "Amount in Cart",
        accessor: (originalRow) => formatMoney(originalRow.totalCartAmount),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "email",
        header: "E-Mail",
        accessor: (originalRow) => originalRow.isEmailSent,
        Cell: ({ row }) => <CellEmail row={row} onSuccess={refetch} />,
        isSortable: false,
        style: styles.tableColumnLg,
      },
      {
        id: "status",
        header: "Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ row }) => (
          <PdBadge colorScheme={row.status === "recovered" ? "green" : "yellow"}>
            <PdBadgeLabel>{row.status === "recovered" ? "Recovered" : "Not Recovered"}</PdBadgeLabel>
          </PdBadge>
        ),
        isSortable: false,
        style: styles.tableColumnMd,
      },
      {
        id: "isPromotionUsed",
        header: "Promotion Used?",
        accessor: (originalRow) => (originalRow.isPromotionUsed ? "Yes" : "No"),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnSm,
      },
    ],
    [handleOnPressToggleAll, isAllSelected, refetch, updateRow, updated],
  );
  const tableInstance = useTable<tAbandonedCart>({
    data: data ?? [],
    columns,
    initialState: {
      sortedBy: "",
      sortDirection: "desc",
    },
  });
  const sendAbandonedCartPromotionEmail = useSendAbandonedCartPromotionEmail();
  const isMobile = useIsMobile();

  const handleOnMassEmail = async () => {
    const response = await sendAbandonedCartPromotionEmail.openAsync({
      cartIds: Object.keys(pickBy(updated, (u) => u.isSelected)),
      brandName: rCompany.type === "brand" ? rCompany.name : "",
      contactFirstName: rCompany.type === "brand" ? rCompany.contactPerson ?? "" : "",
    });
    if (response.status === "closed") return;
    refetch();
    setUpdated({});
  };

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </View>
    );
  }

  return (
    <View
      style={[styles.flex1, isMobile ? [styles.pt4, styles.px4, styles.pb2] : [styles.pt8, styles.px10, styles.pb4]]}
    >
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">Abandoned Carts</PdText>
        {isAtLeastTwoSelected && (
          <PdButton onPress={handleOnMassEmail} colorScheme="green">
            <PdButtonLabel>Mass E-Mail</PdButtonLabel>
          </PdButton>
        )}
      </PdStack>
      <PdText style={styles.subtitle}>
        Recover abandoned shopping carts by sending prewritten emails and optional Private Promos to your customers. A
        mass email to multiple customers can be sent by checking the boxes on the left. Limited to 3 emails per
        retailer.
      </PdText>
      {tableInstance.rows.length === 0 ? (
        <View style={styles.center}>
          <PdText>{`You don't have any abandoned carts`}</PdText>
        </View>
      ) : (
        <PdTable
          ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
          ListEmptyComponent={PdTableEmpty}
          data={tableInstance.rows}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <PdTableRow>
              {item.cells.map((cell) => (
                <PdTableCell key={cell.uniqueId} style={cell.style}>
                  {cell.render("Cell")}
                </PdTableCell>
              ))}
            </PdTableRow>
          )}
        />
      )}
    </View>
  );
};

export const P03_03 = React.memo(P03_03_AbandonedCarts);

const styles = StyleSheet.create({
  alignEnd: {
    alignItems: "flex-end",
  },
  center: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  flex1: {
    flex: 1,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  pb2: {
    paddingBottom: kStyleConst.kSpacing * 2,
  },
  pb4: {
    paddingBottom: kStyleConst.kSpacing * 4,
  },
  pt4: {
    paddingTop: kStyleConst.kSpacing * 4,
  },
  pt8: {
    paddingTop: kStyleConst.kSpacing * 8,
  },
  px10: {
    paddingHorizontal: kStyleConst.kSpacing * 10,
  },
  px4: {
    paddingHorizontal: kStyleConst.kSpacing * 4,
  },
  subtitle: {
    color: kColors.gray500,
    fontStyle: "italic",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  tableColumnLg: {
    width: 220,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 120,
  },
  tableColumnXs: {
    width: 40,
  },
});
